import UploadOutlined from '@ant-design/icons/UploadOutlined'
import { Button, Image, Input, message, Upload } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useRolodexSignedURLs } from 'features/rolodex'
import { ReportFormImageData } from 'pages/reporting/reports/raut/rautReportData'
import React, { useState } from 'react'
import styled from 'styled-components'

const ImageContainer = styled.div`
  margin-bottom: 16px;
`

const StyledImage = styled(Image)`
  max-width: 100%;
  margin-bottom: 16px;
`

interface ImageUploadProps {
  metadataEntryId: string
  imageData?: ReportFormImageData
  onUpload: (path: string, imageKey: string, caption?: string) => void
  fileNamePrefix: string // To create unique file names for different sections
  buttonText?: string
  maxSizeInMB?: number
  acceptedFileTypes?: string[]
  imageKey: string
  allowCaption?: boolean
  subpath?: string
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  metadataEntryId,
  imageData,
  onUpload,
  fileNamePrefix,
  buttonText,
  maxSizeInMB = 5,
  acceptedFileTypes = ['.png', '.jpg', '.jpeg'],
  imageKey,
  allowCaption = false,
  subpath,
}) => {
  const { rolodexUploadFile } = useRolodexEdit()
  const [uploading, setUploading] = useState(false)
  const [caption, setCaption] = useState(imageData?.imageCaption || '')

  const { results: signedUrlResults } = useRolodexSignedURLs(
    imageData?.imagePath
      ? [
          {
            entry_id: metadataEntryId,
            path: imageData.imagePath,
          },
        ]
      : []
  )

  const imageUrl = signedUrlResults[0]?.signed_url

  const handleUpload = async (file: File) => {
    try {
      // Validate file size
      if (file.size > maxSizeInMB * 1024 * 1024) {
        message.error(`File must be smaller than ${maxSizeInMB}MB`)
        return
      }

      // Get file extension and validate file type
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
      if (!acceptedFileTypes.includes(fileExtension)) {
        message.error(
          `File type not accepted. Please upload: ${acceptedFileTypes.join(', ')}`
        )
        return
      }

      setUploading(true)

      const fileName = `${fileNamePrefix}${fileExtension}`

      const imagePath = subpath ? `${subpath}/${fileName}` : fileName

      await rolodexUploadFile(metadataEntryId, file, imagePath)
      onUpload(imagePath, imageKey, caption || undefined)
      message.success('Image uploaded successfully')
    } catch {
      message.error('Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCaption = e.target.value
    setCaption(newCaption)
    if (imageData?.imagePath) {
      onUpload(imageData.imagePath, imageKey, newCaption || undefined)
    }
  }

  const defaultButtonText = imageData?.imagePath
    ? 'Update Image'
    : 'Upload Image'

  return (
    <ImageContainer>
      {imageData?.imagePath && imageUrl && (
        <>
          <StyledImage
            src={imageUrl}
            alt={imageData.imageCaption || 'Uploaded Image'}
          />
          {imageData.imageCaption && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              {imageData.imageCaption}
            </div>
          )}
        </>
      )}
      {allowCaption && (
        <Input
          placeholder='Add image caption'
          value={caption}
          onChange={handleCaptionChange}
          style={{ marginBottom: 8 }}
        />
      )}
      <Upload
        beforeUpload={(file) => {
          handleUpload(file)
          return false
        }}
        accept={acceptedFileTypes.join(',')}
        showUploadList={false}>
        <Button icon={<UploadOutlined />} loading={uploading}>
          {buttonText || defaultButtonText}
        </Button>
      </Upload>
    </ImageContainer>
  )
}
